import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { AppShell } from "@/components/layout/AppShell";
import { BiFilterBar, useBiFilters } from "@/components/bi/BiFilters";
import {
  ChartCard,
  ChartState,
  MetricCard,
  formatMoneyCLP,
  formatNumber,
  tooltipStyle,
} from "@/components/bi/BiPrimitives";
import { estadoLabels } from "@/lib/mock-data";
import { biAnalyticsService } from "@/services/biAnalytics.service";

export const Route = createFileRoute("/bi/pipeline")({
  head: () => ({ meta: [{ title: "Reporte Pipeline — LicitaPro" }] }),
  component: Page,
});

const OPEN_STAGES = ["detectada", "analisis", "viable", "preparacion", "postulada", "evaluacion"];

function stageLabel(stage: string) {
  return (estadoLabels as Record<string, string>)[stage] ?? stage;
}

function Page() {
  const { filters } = useBiFilters();
  const pipeline = useQuery({
    queryKey: ["bi", "pipeline", filters],
    queryFn: () => biAnalyticsService.pipeline(filters),
  });
  const kpis = useQuery({
    queryKey: ["bi", "kpis", filters],
    queryFn: () => biAnalyticsService.kpis(filters),
  });
  const trends = useQuery({
    queryKey: ["bi", "trends", filters],
    queryFn: () => biAnalyticsService.trends(filters),
  });
  const ticket = useQuery({
    queryKey: ["bi", "ticket-ganado-perdido", filters],
    queryFn: () => biAnalyticsService.ticketGanadoPerdido(filters),
  });

  const funnel = (pipeline.data?.funnel ?? []).map((s) => ({
    ...s,
    label: stageLabel(s.stage),
  }));
  const openAmount = funnel
    .filter((s) => OPEN_STAGES.includes(s.stage))
    .reduce((sum, s) => sum + s.amountCLP, 0);

  return (
    <AppShell
      title="Pipeline comercial"
      subtitle="Embudo interno de oportunidades en seguimiento"
      actions={
        <Link
          to="/bi"
          className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border border-border hover:bg-muted text-muted-foreground hover:text-foreground transition"
        >
          <ArrowLeft className="size-3.5" /> Reportes
        </Link>
      }
    >
      <BiFilterBar />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <MetricCard
          label="Oportunidades"
          value={formatNumber(pipeline.data?.total ?? 0)}
          hint={`${formatNumber(pipeline.data?.open ?? 0)} abiertas`}
          loading={pipeline.isLoading}
        />
        <MetricCard
          label="Pipeline abierto (CLP)"
          value={formatMoneyCLP(openAmount)}
          loading={pipeline.isLoading}
        />
        <MetricCard
          label="Postuladas"
          value={formatNumber(kpis.data?.submitted ?? 0)}
          hint={`${formatNumber(kpis.data?.awarded ?? 0)} adjudicadas`}
          loading={kpis.isLoading}
        />
        <MetricCard
          label="Tasa de adjudicación"
          value={kpis.data?.awardRate != null ? `${kpis.data.awardRate}%` : "—"}
          loading={kpis.isLoading}
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mb-4">
        <ChartCard title="Embudo por etapa" subtitle="Oportunidades en cada etapa del pipeline">
          <ChartState loading={pipeline.isLoading} error={pipeline.error} empty={!funnel.length}>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={funnel} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" horizontal={false} />
                <XAxis
                  type="number"
                  stroke="var(--color-muted-foreground)"
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  type="category"
                  dataKey="label"
                  stroke="var(--color-muted-foreground)"
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                  width={110}
                />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="count" name="Oportunidades" fill="var(--color-chart-1)" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartState>
        </ChartCard>

        <ChartCard title="Postulaciones vs adjudicaciones" subtitle="Evolución mensual">
          <ChartState loading={trends.isLoading} error={trends.error} empty={!trends.data?.length}>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={trends.data}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
                <XAxis
                  dataKey="bucket"
                  stroke="var(--color-muted-foreground)"
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  stroke="var(--color-muted-foreground)"
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip contentStyle={tooltipStyle} />
                <Line
                  type="monotone"
                  dataKey="submitted"
                  name="Postuladas"
                  stroke="var(--color-chart-1)"
                  strokeWidth={2.5}
                  dot={{ r: 3 }}
                />
                <Line
                  type="monotone"
                  dataKey="awarded"
                  name="Adjudicadas"
                  stroke="var(--color-chart-2)"
                  strokeWidth={2.5}
                  dot={{ r: 3 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </ChartState>
        </ChartCard>
      </div>

      <ChartCard
        title="Ticket promedio: ganado vs. perdido"
        subtitle="Monto CLP promedio de las oportunidades cerradas, por resultado"
        className="mb-4"
      >
        <ChartState
          loading={ticket.isLoading}
          error={ticket.error}
          empty={!ticket.data || (ticket.data.ganado.count === 0 && ticket.data.perdido.count === 0)}
          height={100}
        >
          <div className="grid grid-cols-2 gap-3">
            <MetricCard
              label="Ganado"
              value={formatMoneyCLP(ticket.data?.ganado.promedio ?? 0)}
              hint={`${formatNumber(ticket.data?.ganado.count ?? 0)} adjudicadas`}
              loading={ticket.isLoading}
            />
            <MetricCard
              label="Perdido"
              value={formatMoneyCLP(ticket.data?.perdido.promedio ?? 0)}
              hint={`${formatNumber(ticket.data?.perdido.count ?? 0)} perdidas`}
              loading={ticket.isLoading}
            />
          </div>
        </ChartState>
      </ChartCard>

      <ChartCard title="Detalle por etapa" subtitle="Monto CLP comprometido y antigüedad promedio">
        <ChartState loading={pipeline.isLoading} error={pipeline.error} empty={!funnel.length} height={200}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-muted-foreground border-b border-border">
                  <th className="text-left font-medium py-2">Etapa</th>
                  <th className="text-right font-medium py-2">Oportunidades</th>
                  <th className="text-right font-medium py-2">Monto CLP</th>
                  <th className="text-right font-medium py-2">Antigüedad prom.</th>
                </tr>
              </thead>
              <tbody>
                {funnel.map((row) => (
                  <tr key={row.stage} className="border-b border-border/60 last:border-0">
                    <td className="py-2 text-foreground">{row.label}</td>
                    <td className="py-2 text-right tabular-nums text-foreground">{row.count}</td>
                    <td className="py-2 text-right tabular-nums text-foreground">
                      {formatMoneyCLP(row.amountCLP)}
                    </td>
                    <td className="py-2 text-right tabular-nums text-muted-foreground">
                      {row.avgAgeDays} días
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </ChartState>
      </ChartCard>
    </AppShell>
  );
}
